import React, { memo, useMemo, type CSSProperties } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { Box, Link, Typography } from '@mui/material'
import type { Components } from 'react-markdown'

type Props = {
    content: string
    /** When true, render plain text (no markdown parse) until streaming finishes. */
    streaming?: boolean
}

const sanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
    attributes: {
        ...defaultSchema.attributes,
        th: [...(defaultSchema.attributes?.th ?? []), 'align'],
        td: [...(defaultSchema.attributes?.td ?? []), 'align'],
        a: [...(defaultSchema.attributes?.a ?? []), 'href', 'title', 'target', 'rel'],
    },
}

function cellTextAlign(style?: CSSProperties, align?: string): CSSProperties['textAlign'] {
    if (style?.textAlign) return style.textAlign
    if (align === 'right' || align === 'center' || align === 'left') return align
    return undefined
}

function MarkdownMessageInner({ content, streaming = false }: Props) {
    const components = useMemo<Components>(
        () => ({
            p: ({ children }) => (
                <Typography variant="body2" component="p" sx={{ mb: 1.25, lineHeight: 1.55, '&:last-child': { mb: 0 } }}>
                    {children}
                </Typography>
            ),
            h1: ({ children }) => (
                <Typography variant="subtitle1" component="h1" fontWeight={600} sx={{ mb: 1, mt: 1.5 }}>
                    {children}
                </Typography>
            ),
            h2: ({ children }) => (
                <Typography variant="subtitle2" component="h2" fontWeight={600} sx={{ mb: 0.75, mt: 1.25 }}>
                    {children}
                </Typography>
            ),
            h3: ({ children }) => (
                <Typography variant="body2" component="h3" fontWeight={600} sx={{ mb: 0.5, mt: 1 }}>
                    {children}
                </Typography>
            ),
            ul: ({ children }) => (
                <Box component="ul" sx={{ pl: 2.5, my: 0.75, '& li': { mb: 0.25 } }}>
                    {children}
                </Box>
            ),
            ol: ({ children }) => (
                <Box component="ol" sx={{ pl: 2.5, my: 0.75, '& li': { mb: 0.25 } }}>
                    {children}
                </Box>
            ),
            li: ({ children }) => (
                <Typography component="li" variant="body2">
                    {children}
                </Typography>
            ),
            a: ({ href, children }) => (
                <Link href={href} target="_blank" rel="noopener noreferrer" variant="body2">
                    {children}
                </Link>
            ),
            code: ({ className, children }) => {
                const isBlock = className?.includes('language-')
                if (isBlock) {
                    return (
                        <Box
                            component="pre"
                            sx={{
                                my: 1,
                                p: 1.25,
                                borderRadius: 1,
                                bgcolor: 'background.default',
                                border: 1,
                                borderColor: 'divider',
                                overflow: 'auto',
                                fontSize: '0.8125rem',
                                fontFamily: 'monospace',
                            }}
                        >
                            <code>{children}</code>
                        </Box>
                    )
                }
                return (
                    <Box
                        component="code"
                        sx={{
                            px: 0.5,
                            py: 0.125,
                            borderRadius: 0.5,
                            bgcolor: 'background.default',
                            border: 1,
                            borderColor: 'divider',
                            fontSize: '0.8125rem',
                            fontFamily: 'monospace',
                        }}
                    >
                        {children}
                    </Box>
                )
            },
            table: ({ children }) => (
                <Box
                    sx={{
                        my: 1.5,
                        overflowX: 'auto',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.8125rem',
                            tableLayout: 'auto',
                            '& th, & td': {
                                borderBottom: 1,
                                borderColor: 'divider',
                                px: 1.25,
                                py: 0.625,
                                verticalAlign: 'middle',
                            },
                            '& tr:last-of-type td': { borderBottom: 0 },
                            '& th': {
                                bgcolor: 'action.hover',
                                fontWeight: 600,
                            },
                            /* Screener tables: symbol left, numeric columns right unless GFM align overrides. */
                            '& th:first-of-type, & td:first-of-type': {
                                textAlign: 'left',
                                pr: 2,
                                whiteSpace: 'nowrap',
                            },
                            '& th:not(:first-of-type), & td:not(:first-of-type)': {
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            ),
            thead: ({ children }) => <thead>{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <tr>{children}</tr>,
            th: ({ children, style, ...props }) => {
                const align = cellTextAlign(style, props.align as string | undefined) ?? undefined
                return (
                    <th
                        style={{
                            ...style,
                            ...(align ? { textAlign: align } : undefined),
                            ...(align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : undefined),
                        }}
                    >
                        {children}
                    </th>
                )
            },
            td: ({ children, style, ...props }) => {
                const align = cellTextAlign(style, props.align as string | undefined) ?? undefined
                return (
                    <td
                        style={{
                            ...style,
                            ...(align ? { textAlign: align } : undefined),
                            ...(align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : undefined),
                        }}
                    >
                        {children}
                    </td>
                )
            },
            blockquote: ({ children }) => (
                <Box
                    component="blockquote"
                    sx={{
                        my: 1,
                        pl: 1.5,
                        borderLeft: 3,
                        borderColor: 'divider',
                        color: 'text.secondary',
                    }}
                >
                    {children}
                </Box>
            ),
            hr: () => <Box component="hr" sx={{ my: 1.5, border: 0, borderTop: 1, borderColor: 'divider' }} />,
            strong: ({ children }) => (
                <Box component="strong" sx={{ fontWeight: 600 }}>
                    {children}
                </Box>
            ),
        }),
        [],
    )

    if (!content.trim()) {
        return null
    }

    if (streaming) {
        return (
            <Typography variant="body2" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word', lineHeight: 1.55 }}>
                {content}
            </Typography>
        )
    }

    return (
        <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
                components={components}
            >
                {content}
            </ReactMarkdown>
        </Box>
    )
}

const MarkdownMessage = memo(MarkdownMessageInner)
export default MarkdownMessage
